/**
 * Pure date utility functions
 * These functions are language-agnostic and handle date calculations and comparisons
 * For formatting dates, use the useLocalizedDate hook instead
 */

/**
 * Check if a date is overdue (in the past)
 * @param date - Date to check
 * @returns true if the date is in the past, false otherwise
 */
export const isDateOverdue = (date: Date | string | null | undefined): boolean => {
  if (!date) return false;

  const targetDate = date instanceof Date ? date : new Date(date);
  if (isNaN(targetDate.getTime())) return false;

  const now = new Date();
  // Set time to end of day for the target date to be more user-friendly
  const endOfTargetDay = new Date(targetDate);
  endOfTargetDay.setHours(23, 59, 59, 999);

  return now > endOfTargetDay;
};

/**
 * Calculate days until a due date
 * @param dueDate - Target date
 * @returns Number of days (positive for future, negative for past)
 */
export const getDaysUntilDue = (dueDate: Date | string | null | undefined): number => {
  if (!dueDate) return 0;

  const targetDate = dueDate instanceof Date ? dueDate : new Date(dueDate);
  if (isNaN(targetDate.getTime())) return 0;

  const now = new Date();
  const timeDiff = targetDate.getTime() - now.getTime();
  return Math.ceil(timeDiff / (1000 * 3600 * 24));
};

/**
 * Calculate the difference between two dates in various units
 * @param date1 - First date
 * @param date2 - Second date
 * @returns Object with difference in various units
 */
export const getDateDifference = (
  date1: Date | string,
  date2: Date | string
): {
  milliseconds: number;
  seconds: number;
  minutes: number;
  hours: number;
  days: number;
  weeks: number;
} => {
  const d1 = date1 instanceof Date ? date1 : new Date(date1);
  const d2 = date2 instanceof Date ? date2 : new Date(date2);

  const milliseconds = Math.abs(d2.getTime() - d1.getTime());

  return {
    milliseconds,
    seconds: Math.floor(milliseconds / 1000),
    minutes: Math.floor(milliseconds / (1000 * 60)),
    hours: Math.floor(milliseconds / (1000 * 60 * 60)),
    days: Math.floor(milliseconds / (1000 * 60 * 60 * 24)),
    weeks: Math.floor(milliseconds / (1000 * 60 * 60 * 24 * 7))
  };
};

/**
 * Check if a date is today
 * @param date - Date to check
 * @returns true if the date is today
 */
export const isToday = (date: Date | string | null | undefined): boolean => {
  if (!date) return false;

  const targetDate = date instanceof Date ? date : new Date(date);
  if (isNaN(targetDate.getTime())) return false;

  const today = new Date();
  return (
    targetDate.getDate() === today.getDate() &&
    targetDate.getMonth() === today.getMonth() &&
    targetDate.getFullYear() === today.getFullYear()
  );
};

/**
 * Check if a date is yesterday
 * @param date - Date to check
 * @returns true if the date is yesterday
 */
export const isYesterday = (date: Date | string | null | undefined): boolean => {
  if (!date) return false;

  const targetDate = date instanceof Date ? date : new Date(date);
  if (isNaN(targetDate.getTime())) return false;

  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);

  return (
    targetDate.getDate() === yesterday.getDate() &&
    targetDate.getMonth() === yesterday.getMonth() &&
    targetDate.getFullYear() === yesterday.getFullYear()
  );
};

/**
 * Check if a date is tomorrow
 * @param date - Date to check
 * @returns true if the date is tomorrow
 */
export const isTomorrow = (date: Date | string | null | undefined): boolean => {
  if (!date) return false;

  const targetDate = date instanceof Date ? date : new Date(date);
  if (isNaN(targetDate.getTime())) return false;

  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);

  return (
    targetDate.getDate() === tomorrow.getDate() &&
    targetDate.getMonth() === tomorrow.getMonth() &&
    targetDate.getFullYear() === tomorrow.getFullYear()
  );
};

/**
 * Check if a date is within the current week
 * @param date - Date to check
 * @param firstDayOfWeek - First day of week (0 = Sunday, 1 = Monday)
 * @returns true if the date is in the current week
 */
export const isThisWeek = (
  date: Date | string | null | undefined,
  firstDayOfWeek: 0 | 1 = 1
): boolean => {
  if (!date) return false;

  const targetDate = date instanceof Date ? date : new Date(date);
  if (isNaN(targetDate.getTime())) return false;

  const today = new Date();
  const dayOfWeek = today.getDay();
  const daysFromStartOfWeek = firstDayOfWeek === 0 ? dayOfWeek : (dayOfWeek + 6) % 7;

  const startOfWeek = new Date(today);
  startOfWeek.setDate(today.getDate() - daysFromStartOfWeek);
  startOfWeek.setHours(0, 0, 0, 0);

  const endOfWeek = new Date(startOfWeek);
  endOfWeek.setDate(startOfWeek.getDate() + 6);
  endOfWeek.setHours(23, 59, 59, 999);

  return targetDate >= startOfWeek && targetDate <= endOfWeek;
};

/**
 * Check if a date is within the current month
 * @param date - Date to check
 * @returns true if the date is in the current month
 */
export const isThisMonth = (date: Date | string | null | undefined): boolean => {
  if (!date) return false;

  const targetDate = date instanceof Date ? date : new Date(date);
  if (isNaN(targetDate.getTime())) return false;

  const today = new Date();
  return (
    targetDate.getMonth() === today.getMonth() &&
    targetDate.getFullYear() === today.getFullYear()
  );
};

/**
 * Check if a date is within the current year
 * @param date - Date to check
 * @returns true if the date is in the current year
 */
export const isThisYear = (date: Date | string | null | undefined): boolean => {
  if (!date) return false;

  const targetDate = date instanceof Date ? date : new Date(date);
  if (isNaN(targetDate.getTime())) return false;

  const today = new Date();
  return targetDate.getFullYear() === today.getFullYear();
};

/**
 * Add days to a date
 * @param date - Base date
 * @param days - Number of days to add (can be negative)
 * @returns New date with days added
 */
export const addDays = (date: Date | string, days: number): Date => {
  const result = date instanceof Date ? new Date(date) : new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

/**
 * Subtract days from a date
 * @param date - Base date
 * @param days - Number of days to subtract
 * @returns New date with days subtracted
 */
export const subtractDays = (date: Date | string, days: number): Date => {
  return addDays(date, -days);
};

/**
 * Add months to a date
 * @param date - Base date
 * @param months - Number of months to add (can be negative)
 * @returns New date with months added
 */
export const addMonths = (date: Date | string, months: number): Date => {
  const result = date instanceof Date ? new Date(date) : new Date(date);
  result.setMonth(result.getMonth() + months);
  return result;
};

/**
 * Add years to a date
 * @param date - Base date
 * @param years - Number of years to add (can be negative)
 * @returns New date with years added
 */
export const addYears = (date: Date | string, years: number): Date => {
  const result = date instanceof Date ? new Date(date) : new Date(date);
  result.setFullYear(result.getFullYear() + years);
  return result;
};

/**
 * Get the start of day for a date (00:00:00.000)
 * @param date - Input date
 * @returns New date set to start of day
 */
export const startOfDay = (date: Date | string): Date => {
  const result = date instanceof Date ? new Date(date) : new Date(date);
  result.setHours(0, 0, 0, 0);
  return result;
};

/**
 * Get the end of day for a date (23:59:59.999)
 * @param date - Input date
 * @returns New date set to end of day
 */
export const endOfDay = (date: Date | string): Date => {
  const result = date instanceof Date ? new Date(date) : new Date(date);
  result.setHours(23, 59, 59, 999);
  return result;
};

/**
 * Get the start of week for a date
 * @param date - Input date
 * @param firstDayOfWeek - First day of week (0 = Sunday, 1 = Monday)
 * @returns New date set to start of week
 */
export const startOfWeek = (date: Date | string, firstDayOfWeek: 0 | 1 = 1): Date => {
  const result = date instanceof Date ? new Date(date) : new Date(date);
  const dayOfWeek = result.getDay();
  const daysFromStartOfWeek = firstDayOfWeek === 0 ? dayOfWeek : (dayOfWeek + 6) % 7;

  result.setDate(result.getDate() - daysFromStartOfWeek);
  return startOfDay(result);
};

/**
 * Get the end of week for a date
 * @param date - Input date
 * @param firstDayOfWeek - First day of week (0 = Sunday, 1 = Monday)
 * @returns New date set to end of week
 */
export const endOfWeek = (date: Date | string, firstDayOfWeek: 0 | 1 = 1): Date => {
  const start = startOfWeek(date, firstDayOfWeek);
  const result = new Date(start);
  result.setDate(start.getDate() + 6);
  return endOfDay(result);
};

/**
 * Get the start of month for a date
 * @param date - Input date
 * @returns New date set to start of month
 */
export const startOfMonth = (date: Date | string): Date => {
  const result = date instanceof Date ? new Date(date) : new Date(date);
  result.setDate(1);
  return startOfDay(result);
};

/**
 * Get the end of month for a date
 * @param date - Input date
 * @returns New date set to end of month
 */
export const endOfMonth = (date: Date | string): Date => {
  const result = date instanceof Date ? new Date(date) : new Date(date);
  result.setMonth(result.getMonth() + 1, 0); // 0th day of next month = last day of current month
  return endOfDay(result);
};

/**
 * Check if a year is a leap year
 * @param year - Year to check
 * @returns true if the year is a leap year
 */
export const isLeapYear = (year: number): boolean => {
  return (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
};

/**
 * Get the number of days in a month
 * @param year - Year
 * @param month - Month (0-11)
 * @returns Number of days in the month
 */
export const getDaysInMonth = (year: number, month: number): number => {
  return new Date(year, month + 1, 0).getDate();
};

/**
 * Check if two dates are the same day
 * @param date1 - First date
 * @param date2 - Second date
 * @returns true if both dates are on the same day
 */
export const isSameDay = (
  date1: Date | string | null | undefined,
  date2: Date | string | null | undefined
): boolean => {
  if (!date1 || !date2) return false;

  const d1 = date1 instanceof Date ? date1 : new Date(date1);
  const d2 = date2 instanceof Date ? date2 : new Date(date2);

  if (isNaN(d1.getTime()) || isNaN(d2.getTime())) return false;

  return (
    d1.getDate() === d2.getDate() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getFullYear() === d2.getFullYear()
  );
};

/**
 * Check if two dates are in the same week
 * @param date1 - First date
 * @param date2 - Second date
 * @param firstDayOfWeek - First day of week (0 = Sunday, 1 = Monday)
 * @returns true if both dates are in the same week
 */
export const isSameWeek = (
  date1: Date | string | null | undefined,
  date2: Date | string | null | undefined,
  firstDayOfWeek: 0 | 1 = 1
): boolean => {
  if (!date1 || !date2) return false;

  const d1 = date1 instanceof Date ? date1 : new Date(date1);
  const d2 = date2 instanceof Date ? date2 : new Date(date2);

  if (isNaN(d1.getTime()) || isNaN(d2.getTime())) return false;

  const startOfWeek1 = startOfWeek(d1, firstDayOfWeek);
  const startOfWeek2 = startOfWeek(d2, firstDayOfWeek);

  return isSameDay(startOfWeek1, startOfWeek2);
};

/**
 * Check if two dates are in the same month
 * @param date1 - First date
 * @param date2 - Second date
 * @returns true if both dates are in the same month
 */
export const isSameMonth = (
  date1: Date | string | null | undefined,
  date2: Date | string | null | undefined
): boolean => {
  if (!date1 || !date2) return false;

  const d1 = date1 instanceof Date ? date1 : new Date(date1);
  const d2 = date2 instanceof Date ? date2 : new Date(date2);

  if (isNaN(d1.getTime()) || isNaN(d2.getTime())) return false;

  return d1.getMonth() === d2.getMonth() && d1.getFullYear() === d2.getFullYear();
};

/**
 * Validate if a date string is valid
 * @param dateString - Date string to validate
 * @returns true if the date string represents a valid date
 */
export const isValidDate = (dateString: string | null | undefined): boolean => {
  if (!dateString) return false;

  const date = new Date(dateString);
  return !isNaN(date.getTime());
};

/**
 * Clamp a date between min and max dates
 * @param date - Date to clamp
 * @param min - Minimum allowed date
 * @param max - Maximum allowed date
 * @returns Clamped date
 */
export const clampDate = (
  date: Date | string,
  min?: Date | string | null,
  max?: Date | string | null
): Date => {
  let result = date instanceof Date ? new Date(date) : new Date(date);

  if (min) {
    const minDate = min instanceof Date ? min : new Date(min);
    if (result < minDate) result = new Date(minDate);
  }

  if (max) {
    const maxDate = max instanceof Date ? max : new Date(max);
    if (result > maxDate) result = new Date(maxDate);
  }

  return result;
};

/**
 * Convert Date object to ISO date string (YYYY-MM-DD format)
 * Defensive: Also handles ISO strings from localStorage (dates not yet rehydrated)
 * @param date - Date to convert (Date object or ISO string)
 * @returns ISO date string or null if date is null/invalid
 */
export const dateToISOString = (date: Date | string | null | undefined): string | null => {
  if (!date) return null;

  // Handle both Date objects and ISO strings (defensive coding)
  const dateObj = date instanceof Date ? date : new Date(date);

  if (isNaN(dateObj.getTime())) return null;

  return dateObj.toISOString().split('T')[0] || null;
};